import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import StringToComponent from '../../../../lib/stringToComponent';
import DetailsHeader from '../../components/DetailsHeader';
import DetailsTwofers from '../../components/DetailsTwofers';
import DetailsCrossSell from '../../components/DetailsCrossSell';
import DetailsFooter from '../../components/DetailsFooter';
import RecommendedRow from '../../components/RecommendedRow';
import { LoadingSpinner } from '@paypalcorp/pp-react';
import fireBatchedImpressions from '../../lib/BatchImpressions';
import { cloneDeep, isEmpty } from 'lodash';
import { clientLogger } from '../../../../lib/logger';
import { isDetailsClickGetStarted } from '../../../../lib/amplitude';
import { AMPLITUDE } from '../../../../lib/constants';
const { PRIMARY_CTA_CLICK } = AMPLITUDE;

const setStaticContextUrl = staticContext => {
  staticContext.url = staticContext?.req?.user
    ? '/merchantapps/appcenter'
    : '/merchantapps/appcenter/notfound';
};

const buildLayout = (appKey, layouts) => {
  let layout = '';
  if (appKey) {
    ({ layout } = layouts.find(objLayout => objLayout.appKey === appKey) || {});
  }
  return layout;
};

//exported for test case
export const handleErrorFPTI = (errorMessage, fptiImpression) => {
  if (typeof window === 'undefined' || typeof fptiImpression !== 'function') {
    return;
  }
  const feedback = window.feedback || {};

  fptiImpression({
    ...feedback,
    erpg: errorMessage,
    eccd: 'code'
  });
};

//exported for test case
export const handleClickFPTI = (detailFPTI, appKey, fptiClick) => {
  const options = { fptiLink: detailFPTI };

  if (isDetailsClickGetStarted(detailFPTI, appKey)) {
    // enable amplitude (atm only for header get_started clicks)
    options.amplitude = true;
    options.event_name = PRIMARY_CTA_CLICK;
  }

  fptiClick(options);
};

const constructLayout = (layout, psResponse, appKey, fptiImpression) => {
  const componentsContent = psResponse.details
    ? cloneDeep(psResponse.details)
    : [];
  return layout
    .split(',')
    .map(s => {
      let finalContent;
      // Get the content related to the particular tool
      if (componentsContent[s]) {
        componentsContent[s] = componentsContent[s].filter(
          o => o.toolKey === appKey
        );
        // Sort by order for tools that have the order prop
        finalContent = componentsContent[s]
          .sort((a, b) => a.order - b.order)
          .shift();
      }

      if (!finalContent) {
        handleErrorFPTI(
          `For ${appKey} : ${s} content is missing`,
          fptiImpression
        );
      }
      return {
        Component: StringToComponent[s],
        content: finalContent
      };
    })
    .filter(i => i.Component && i.content);
};

const setLayout = (props, location, appKey) => {
  const { flatPSData = {}, staticContext = {}, fptiImpression } = props;
  if (isEmpty(flatPSData)) {
    return [];
  }
  const { layouts = [] } = flatPSData;
  const layout = buildLayout(appKey, layouts);

  if ((!appKey || !layout) && typeof window === 'undefined') {
    clientLogger.warn(
      `${location} Redirecting: Details layout or appKey is missing`,
      JSON.stringify({
        country: props.app?.legalCountry,
        locale: props.app?.context?.locale?.locale,
        loggedIn: props.app?.fptiData?.lgin
      })
    );
    setStaticContextUrl(staticContext);
  }
  //Layout found
  if (layout && layout.length) {
    return constructLayout(layout, flatPSData, appKey, fptiImpression);
  }
  // No Layout found default to header
  const {
    detailsContent: { DetailsHeader: detailsHeaderContent = [] } = {}
  } = props;
  // if no layout default to just the header component
  let fallbackLayout = [
    {
      Component: DetailsHeader,
      content: detailsHeaderContent.find(o => o.toolKey === appKey)
    }
  ];

  if (
    (fallbackLayout.length < 1 || !fallbackLayout[0]?.content) &&
    typeof window === 'undefined'
    // server-side check
  ) {
    handleErrorFPTI(
      `${location} Details Layout or content is missing`,
      fptiImpression
    );
    clientLogger.error(
      `${location} Details layout or content is missing`,
      JSON.stringify({
        country: props.app?.legalCountry,
        locale: props.app?.context?.locale?.locale,
        loggedIn: props.app?.fptiData?.lgin
      })
    );
  } else if (
    fallbackLayout.length &&
    !fallbackLayout[0]?.content &&
    typeof window !== 'undefined'
  ) {
    // client side check for fetching content on click
    fallbackLayout = [];
  }
  return fallbackLayout;
};

const Details = props => {
  const {
    location: { pathname = '' } = {},
    flatPSData = {},
    fptiClick,
    experimentation: { recommendationsOnDetailsPage } = {},
    toolTileProps: {
      app: { context: { user: { isLoggedIn } = {} } = {} } = {},
      flatPSData: { isRecommendedCategoryEligible } = {}
    } = {}
  } = props;

  const location = pathname.split('/').pop();
  // Get the current tool name using the location from above
  // Use that tool name to pull out the layout array
  // Use the layout array to by iterating them and the details content
  // Pass only relevant content per component]
  const { tools = {} } = flatPSData;
  const { appKey = '' } =
    Object.values(tools).find(objTool => location === objTool.location) || {};

  const [layoutState, updateLayoutState] = useState(
    setLayout(props, location, appKey)
  );

  const renderYourRecommendations = toolKey => {
    const {
      fptiClick,
      fptiImpression,
      toolTileProps,
      defaultRecommended = []
    } = props;
    const {
      flatPSData: { recommended = [], isNewLayoutEligible } = {}
    } = toolTileProps;
    const categoryPageLink = 'details_page_recommendation';
    // Remove the tool for which detail page is rendered from recommendation.
    const filteredRecommendation = defaultRecommended.filter(
      t => t.appKey !== toolKey
    );

    // Do not render recommendations if we have less than 6 tools
    if (filteredRecommendation.length < 6) {
      return null;
    }

    return (
      <div
        id="appcenter-categories"
        className={isNewLayoutEligible ? 'mobile-bkg' : ''}
      >
        <RecommendedRow
          content={recommended[0]}
          featured={filteredRecommendation}
          fptiClick={fptiClick}
          fptiImpression={fptiImpression}
          toolTileProps={toolTileProps}
          largeTile={false}
          categoryPageLink={categoryPageLink}
        />
        <hr className="nav-divider" />
      </div>
    );
  };

  useEffect(() => {
    if (layoutState && layoutState.length < 1) {
      const newLayout = setLayout(props, location, appKey);
      if (newLayout && newLayout.length > 0) {
        updateLayoutState(newLayout);
      }
    }
  }, [props.flatPSData.layouts]);

  useEffect(() => {
    // this is an over-engineered way of giving each details photo section component a unique id
    // so that we can assign them unique/ numbered 1, 2, 3 ... fpti clicks
    const detailsPhotoSections =
      document.querySelectorAll('[id*="details-options"]') || [];
    detailsPhotoSections.forEach((component, index) => {
      component.id = `details-options-${index + 1}`;
    });

    const links = [];
    layoutState.forEach(({ content = {} }) => {
      links.push(content.PSImpressionUrl);
    });
    if (links.length) {
      fireBatchedImpressions(links, props.app.csrf);
    }
  }, [layoutState]);

  // Used for generating default alt tags for all images in child components
  const toolName = props.tools[layoutState[0]?.content?.toolKey]?.name;
  if (layoutState.length < 1 || !layoutState[0]?.content) {
    return (
      <div className="spinner-container">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Remove Cross-Sell and Twofers in favor of RFY on Details Page
  const filteredLayoutState = layoutState.filter(l => {
    return l.Component !== DetailsCrossSell && l.Component !== DetailsTwofers;
  });

  return (
    <div id="appcenter-details">
      {filteredLayoutState.map(({ Component, content }, index) => {
        return (
          <Fragment>
            {Component === DetailsFooter &&
              recommendationsOnDetailsPage &&
              isLoggedIn &&
              isRecommendedCategoryEligible &&
              renderYourRecommendations(content.toolKey)}
            <Component
              key={`appcenter-component-item-${index}`}
              componentContent={content}
              handleClickFPTI={detailFPTI =>
                handleClickFPTI(detailFPTI, appKey, fptiClick)
              }
              toolName={toolName}
              {...props}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

Details.displayName = 'details';

Details.propTypes = {
  categories: PropTypes.array,
  layoutForStorybook: PropTypes.string,
  location: PropTypes.object,
  detailsContent: PropTypes.object,
  fptiClick: PropTypes.func,
  app: PropTypes.object,
  flatPSData: PropTypes.object,
  toolTileProps: PropTypes.object,
  experimentation: PropTypes.object,
  defaultRecommended: PropTypes.array,
  fptiImpression: PropTypes.func,
  tools: PropTypes.object,
  staticContext: PropTypes.object,
  isTwofersEligible: PropTypes.boolean
};

export default Details;
